import { useEffect, useRef, useCallback, useState } from "react";
import { Box, Drawer, IconButton, Typography, TextField, Button, useTheme, Tooltip, CircularProgress, List, ListItem, ListItemText, Chip, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import ieqipCoreServiceApi from "../apis/ieqipCoreServiceApi";
import NoteEditor from "./NoteEditor";

const NotesDrawer = ({ open, onClose, triggerRef }) => {
  const theme = useTheme();
  const firstFocusableRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  const tenantId = useSelector((state) => state.tenantId);
  const idToken = useSelector((state) => state.token);

  const handleClose = useCallback(() => {
    setIsEditing(false);
    setSelectedNote(null);
    onClose();
    if (triggerRef && triggerRef.current) {
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, [onClose, triggerRef]);

  const handleNewNote = () => {
    setSelectedNote(null);
    setIsEditing(true);
  };

  const handleEditNote = (note) => {
    setSelectedNote(note);
    setIsEditing(true);
  };

  const handleSaveNote = () => {
    setIsEditing(false);
    setSelectedNote(null);
    fetchNotes();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedNote(null);
  };

  const handleDeleteNote = async (note) => {
    if (!note?.user_annotation_uid) return;
    
    const confirmDelete = window.confirm(`Are you sure you want to delete "${note.title}"? This action cannot be undone.`);
    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      const requestHeader = {
        headers: {
          "x-eqip-tenantid": tenantId,
          Authorization: idToken,
        },
      };

      await ieqipCoreServiceApi.delete(`/api/annotations/${note.user_annotation_uid}`, requestHeader);
      fetchNotes(); // Refresh the list
    } catch (error) {
      console.error("Failed to delete note:", error);
      setErrorMessage(error?.response?.data?.message || error.message || "Failed to delete note");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }
  }, [open]);

  const fetchNotes = useCallback(async () => {
    if (!open || !tenantId || !idToken) return;
    
    try {
      setIsLoading(true);
      setErrorMessage(null);
      
      const requestHeader = {
        headers: {
          "x-eqip-tenantid": tenantId,
          // Authorization: idToken,
        },
      };

      const response = await ieqipCoreServiceApi.get(`/api/annotations?sort=created_date_desc`, requestHeader);
      console.log("NotesDrawer.response", response);
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      
      // Sort by updatedAt desc on client side
      items.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.updatedAt || 0).getTime();
        const dateB = new Date(b.updated_at || b.updatedAt || 0).getTime();
        return dateB - dateA;
      });

      setNotes(items);
      setTotal(items.length);
    } catch (error) {
      console.error("Failed to fetch notes:", error);
      // Don't show error for 404 or if endpoint doesn't exist yet
      if (error?.response?.status === 404) {
        setNotes([]);
        setTotal(0);
      } else {
        setErrorMessage(error?.response?.data?.message || error.message || "Failed to load notes");
      }
    } finally {
      setIsLoading(false);
    }
  }, [open, tenantId, idToken]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100%",
        },
      }}
    >
      <Box role="dialog" aria-label="Global notes" display="flex" flexDirection="column" height="100%">
        {!isEditing ? (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1} borderBottom={`1px solid ${theme.palette.divider}`}>
              <Typography variant="h6" component="h2">Notes</Typography>
              <Tooltip title="Close">
                <IconButton aria-label="Close notes" onClick={handleClose} ref={firstFocusableRef}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box component="section" px={2} py={2} display="flex" gap={1} alignItems="center">
              <TextField
                aria-label="Search notes"
                placeholder="Search notes"
                size="small"
                fullWidth
              />
              <Button 
                aria-label="Create new note" 
                color="primary"
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleNewNote}
                sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
              >
                New
              </Button>
            </Box>

        <Box component="section" px={2} pb={2} sx={{ overflowY: "auto", flex: 1 }}>
          {isLoading && (
            <Box display="flex" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}
          
          {!isLoading && errorMessage && (
            <Typography color="error" variant="body2" role="alert">
              {errorMessage}
            </Typography>
          )}
          
          {!isLoading && !errorMessage && notes.length === 0 && (
            <Typography variant="body2" color="text.secondary" tabIndex={0} aria-label="Notes list placeholder">
              Notes will appear here. Use search or create a new note.
            </Typography>
          )}
          
          {!isLoading && !errorMessage && notes.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Total: {total}
              </Typography>
              <List dense disablePadding>
                {notes.map((note, index) => {
                  const isRecent = index < 5;
                  const title = note.title || note.content?.substring(0, 50) || `Note #${index + 1}`;
                  const owner = note.email || note.owner || note.created_by || "Unknown";
                  const updatedRaw = note.updated_at || note.updatedAt || note.created_at || note.createdAt;
                  const updated = updatedRaw ? new Date(updatedRaw).toLocaleString() : "";
                  
                  return (
                    <Box 
                      key={note.id || note.uid || index} 
                      sx={{ 
                        backgroundColor: isRecent ? theme.palette.action.hover : "transparent", 
                        borderRadius: 1, 
                        mb: 0.5,
                        border: isRecent ? `1px solid ${theme.palette.primary.main}` : "1px solid transparent"
                      }}
                    >
                      <ListItem 
                        alignItems="flex-start"
                        sx={{ py: 1 }}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="delete note"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note);
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          onClick={() => handleEditNote(note)}
                          sx={{ cursor: "pointer", pr: 2 }}
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" component="span" sx={{ fontWeight: isRecent ? 600 : 400 }}>
                                {title}
                              </Typography>
                              {isRecent && <Chip size="small" color="primary" label="Recent" aria-label="Recent note" />}
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography component="span" variant="caption" color="text.secondary">
                                {owner}
                              </Typography>
                              {updated && (
                                <>
                                  <Typography component="span" variant="caption" color="text.secondary"> • </Typography>
                                  <Typography component="span" variant="caption" color="text.secondary" aria-label={`Updated at ${updated}`}>
                                    {updated}
                                  </Typography>
                                </>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                      {index < notes.length - 1 && <Divider component="div" />}
                    </Box>
                  );
                })}
              </List>
            </>
          )}
        </Box>
          </>
        ) : (
          <NoteEditor
            note={selectedNote}
            onSave={handleSaveNote}
            onCancel={handleCancelEdit}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default NotesDrawer;


